/**
 * The archipelago is generated from the content — until somebody draws over it.
 *
 * By default an island's shape, its beach, the path through it and where each marker stands are
 * all derived from its id and its list of nodes. That is the difference between "adding island 4
 * is a form the admin fills in" and "adding island 4 needs a tilemap editor and an artist", and it
 * stays the default for exactly that reason: a new island still costs one form.
 *
 * An island with a `world` on it was drawn in the builder, and then the drawing is the truth —
 * terrain, where every marker stands, who is standing on the beach. The two paths meet here, in
 * one function, producing one tilemap the renderer cannot tell apart. The builder itself seeds a
 * new drawing FROM the generator, so nobody ever starts on an empty grid.
 *
 * Generation is deterministic: the same content always produces the same coastline, so the map a
 * student learns on Monday is the map they come back to on Friday.
 */

import type {
  CharacterId,
  PropId,
  ResolvedCharacter,
  ResolvedIsland,
  ResolvedNode,
} from '../../core/content';
import {
  FREE_ISLAND_COLUMNS,
  GENERATED_COLUMNS,
  MAX_ISLAND_SIZE,
  MIN_ISLAND_SIZE,
  SOLID,
  generatedIslandRadius,
  generatedPerRow,
  Terrain,
  WATER,
  isSolid,
  isStandable,
  isWater,
  nearestStandable,
} from '../../core/terrain';

export const TILE = 16;

export { SOLID, Terrain, WATER };

export interface Marker {
  nodeId: string;
  islandId: string;
  x: number;
  y: number;
  kind: ResolvedNode['kind'];
}

/** Somebody the admin placed, in world tiles rather than island tiles. */
export interface PlacedCharacter {
  id: string;
  islandId: string;
  art: CharacterId;
  name: string | null;
  facing: ResolvedCharacter['facing'];
  lines: string[];
  x: number;
  y: number;
}

export interface PlacedProp {
  kind: PropId;
  x: number;
  y: number;
}

export interface IslandPlacement {
  islandId: string;
  name: string;
  biome: string;
  /** Tile the player stands on when they arrive. */
  dock: { x: number; y: number };
  /** Where the island's name is drawn. */
  label: { x: number; y: number };
  bounds: { x0: number; y0: number; x1: number; y1: number };
  landmark?: { x: number; y: number };
  locked: boolean;
}

export interface World {
  width: number;
  height: number;
  tiles: Uint8Array;
  markers: Marker[];
  islands: IslandPlacement[];
  characters: PlacedCharacter[];
  props: PlacedProp[];
  origin: { x: number; y: number };
  signature: string;
}

/* ------------------------------------------------------------------ deterministic noise */

function hash(x: number, y: number, seed: number): number {
  let h = (x * 374761393 + y * 668265263 + seed * 2246822519) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 1274126177) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function seedOf(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/* ------------------------------------------------------------------ generation */

const MARGIN = 6;

interface Draft {
  tiles: Map<number, Terrain>;
  markers: Marker[];
  islands: IslandPlacement[];
  characters: PlacedCharacter[];
  props: PlacedProp[];
}

/**
 * The rectangle an island occupies on the archipelago, whichever way it came into being.
 *
 * A drawn island is its canvas, centred on `mapa` — the same point a generated island's ellipse is
 * centred on. That is deliberate and it is the whole reason `mapa` was left meaning "centre":
 * drawing over a generated island leaves it in the same patch of sea it was already in, so the
 * archipelago does not rearrange itself the first time somebody picks up the brush.
 *
 * The size comes from the island's FULL node list rather than from the viewer's, so a locked island
 * does not grow the day it opens and two paths do not disagree about where the coast is.
 */
function islandExtent(island: ResolvedIsland): { x0: number; y0: number; x1: number; y1: number } {
  if (island.world) {
    const left = island.map.x - Math.floor(island.world.width / 2);
    const top = island.map.y - Math.floor(island.world.height / 2);
    return { x0: left, y0: top, x1: left + island.world.width - 1, y1: top + island.world.height - 1 };
  }
  const shape = islandShape(island, island.layoutIds.length);
  return {
    x0: island.map.x - shape.radiusX,
    y0: island.map.y - shape.radiusY,
    x1: island.map.x + shape.radiusX,
    y1: island.map.y + shape.radiusY + 1,
  };
}

export function buildWorld(
  islands: Array<{ island: ResolvedIsland; unlocked: boolean; visibleNodes: ResolvedNode[] }>,
): World {
  const draft: Draft = { tiles: new Map(), markers: [], islands: [], characters: [], props: [] };

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const entry of islands) {
    const extent = islandExtent(entry.island);
    minX = Math.min(minX, extent.x0 - 3);
    maxX = Math.max(maxX, extent.x1 + 3);
    minY = Math.min(minY, extent.y0 - 3);
    maxY = Math.max(maxY, extent.y1 + 3);
  }

  if (!Number.isFinite(minX)) {
    minX = 0;
    maxX = 20;
    minY = 0;
    maxY = 14;
  }

  const origin = { x: Math.floor(minX) - MARGIN, y: Math.floor(minY) - MARGIN };
  const width = Math.ceil(maxX) - origin.x + MARGIN;
  const height = Math.ceil(maxY) - origin.y + MARGIN;

  for (const entry of islands) {
    if (entry.island.world)
      stampIsland(draft, entry.island, entry.visibleNodes, entry.unlocked, origin);
    // The layout is every node on the island; only the viewer's own get a signpost.
    else
      carveIsland(
        draft,
        entry.island,
        entry.island.layoutIds,
        new Map(entry.visibleNodes.map((node) => [node.id, node.kind] as const)),
        entry.unlocked,
        origin,
      );
  }

  const tiles = new Uint8Array(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const drafted = draft.tiles.get(y * 10000 + x);
      tiles[y * width + x] = drafted ?? seaAt(x, y, width, height);
    }
  }

  const withShore = applyShoreline(tiles, width, height);

  return {
    width,
    height,
    tiles: withShore,
    markers: draft.markers,
    islands: draft.islands,
    characters: draft.characters,
    props: draft.props,
    origin,
    signature: islands
      .map(
        (e) =>
          // Everything the tilemap is derived from, spelled out. The drawing is in here because
          // repainting a beach has to rebuild it and nothing else about the island would say so;
          // the centre is in here because moving an island in the builder otherwise left the map
          // rendering at the old coordinates; and the node ids are listed rather than counted
          // because a publish that swaps one node for another changes where the signposts are
          // without changing how many there are.
          `${e.island.id}:${e.unlocked ? 1 : 0}:${e.island.map.x},${e.island.map.y}:` +
          `${e.island.layoutIds.length}:${e.visibleNodes.map((n) => n.id).join('.')}:` +
          worldStamp(e.island),
      )
      .join('|'),
  };
}

/**
 * A short, stable fingerprint of an island's drawing. Cheap enough to compute on every render and
 * specific enough that any edit in the builder changes it — which is what makes the map in the
 * preview repaint the instant a tile is painted.
 */
function worldStamp(island: ResolvedIsland): string {
  const drawn = island.world;
  if (!drawn) return 'auto';
  let sum = 2166136261;
  for (let index = 0; index < drawn.tiles.length; index++) {
    sum ^= drawn.tiles[index]!;
    sum = Math.imul(sum, 16777619);
  }
  const people = drawn.characters.map((p) => `${p.id}@${p.x},${p.y}:${p.art}:${p.facing}`).join(',');
  const props = drawn.props.map((p) => `${p.kind}@${p.x},${p.y}`).join(',');
  const spots = [...drawn.spots].map(([id, spot]) => `${id}@${spot.x},${spot.y}`).join(',');
  return `${drawn.width}x${drawn.height}:${(sum >>> 0).toString(36)}:${drawn.dock.x},${drawn.dock.y}:${spots}:${people}:${props}`;
}

/**
 * Shallow water hugs the coast — it is what makes the islands read as islands rather than as
 * shapes floating on a flat blue rectangle.
 *
 * Exported because the world builder has to show it. A preview that skips this pass shows the
 * admin an island ringed in navy that turns turquoise the moment it is published, and then the
 * builder is a thing you have to publish to see, which is the one thing it must not be.
 */
export function applyShoreline(tiles: Uint8Array, width: number, height: number): Uint8Array {
  const out = new Uint8Array(tiles);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const index = y * width + x;
      if (!isWater(tiles[index] as Terrain)) continue;
      let nearLand = false;
      for (let dy = -2; dy <= 2 && !nearLand; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
          const neighbour = tiles[ny * width + nx] as Terrain;
          if (!isWater(neighbour) && neighbour !== Terrain.Fog) {
            nearLand = true;
            break;
          }
        }
      }
      if (nearLand) out[index] = Terrain.Shallow;
    }
  }
  return out;
}

function seaAt(x: number, y: number, width: number, height: number): Terrain {
  // The rim of the generated area is fog: open sea the student cannot cross yet. Not grey shapes,
  // not locked icons — nothing (GDD §3).
  //
  // The boundary is nudged by a little noise. A perfectly rectangular band of dark water reads as
  // the edge of a texture; a ragged one reads as distance.
  const edge = Math.min(x, y, width - 1 - x, height - 1 - y);
  const wobble = hash(x, y, 4242) * 2.2;
  if (edge <= 1) return Terrain.Fog;
  if (edge <= 2 + wobble) return Terrain.Deep;
  return Terrain.Sea;
}

/**
 * The parts of an island the generator actually reads.
 *
 * Narrowed from ResolvedIsland so the world builder can seed a canvas from a draft island that has
 * not been resolved for any particular path — the seed has to place EVERY node on the island, not
 * only the ones one path can see, or switching path would reveal a node standing nowhere.
 */
type IslandSeed = Pick<ResolvedIsland, 'id' | 'name' | 'biome' | 'map'> & { free?: boolean };
type NodeSeed = Pick<ResolvedNode, 'id' | 'kind'>;

/**
 * How many markers this island puts in a row. A course island has always been three; a free island
 * holds a whole exercise catalogue and grows sideways instead of running off the bottom of the
 * world. See `generatedIslandRadius`.
 */
export function columnsFor(island: IslandSeed): number {
  return island.free ? FREE_ISLAND_COLUMNS : GENERATED_COLUMNS;
}

function islandShape(island: IslandSeed, nodeCount: number) {
  // Bigger curricula get bigger islands. REZ has eleven nodes and should feel like it. The radii
  // come from core so the publish gate can compute the same footprint without a canvas.
  const columns = columnsFor(island);
  const { radiusX, radiusY } = generatedIslandRadius(nodeCount, columns);
  return {
    seed: seedOf(island.id),
    radiusX,
    radiusY,
    columns,
    rows: Math.max(2, Math.ceil(nodeCount / columns)),
  };
}

function set(draft: Draft, x: number, y: number, terrain: Terrain, force = false): void {
  const key = y * 10000 + x;
  if (!force) {
    const existing = draft.tiles.get(key);
    if (existing !== undefined && existing > terrain) return;
  }
  draft.tiles.set(key, terrain);
}

/**
 * Grow an island out of its node list.
 *
 * `layout` is every node on the island across every path, in authored order, and it decides the
 * whole shape: how wide the ellipse is, where the trail runs, which tile each task stands on.
 * `markers` is the subset this viewer actually has, and it decides only which of those tiles gets a
 * signpost.
 *
 * Splitting the two is what lets an ADOBE member and a DaVinci member walk the same island. They
 * have different task lists — TEMELJ has six on one path and five on the other — and if the shape
 * followed the viewer's list, the same island would be nineteen tiles wide for one of them and
 * seventeen for the other. Everything derived from it would drift with it: the coastline, the ring
 * of trees, the dock, the spacing of the trail. A shared task would sit on a different tile for
 * each of them, and there would be no honest way to say where anybody is standing.
 *
 * So the ground is the island's, not the viewer's. A task only one path has leaves a trodden spot
 * on the trail that the other path simply walks over.
 */
function carveIsland(
  draft: Draft,
  island: IslandSeed,
  layout: readonly string[],
  markers: ReadonlyMap<string, ResolvedNode['kind']>,
  unlocked: boolean,
  origin: { x: number; y: number },
): void {
  const shape = islandShape(island, layout.length);
  const cx = island.map.x - origin.x;
  const cy = island.map.y - origin.y;
  const { seed, radiusX, radiusY, columns } = shape;

  const inside = (x: number, y: number, shrink: number): boolean => {
    const dx = (x - cx) / (radiusX - shrink);
    const dy = (y - cy) / (radiusY - shrink);
    const wobble = 0.16 * Math.sin((x + y) * 0.7 + seed % 7) + 0.12 * (hash(x, y, seed) - 0.5);
    return dx * dx + dy * dy <= 1 + wobble;
  };

  for (let y = cy - radiusY - 2; y <= cy + radiusY + 2; y++) {
    for (let x = cx - radiusX - 2; x <= cx + radiusX + 2; x++) {
      if (!inside(x, y, 0)) continue;
      set(draft, x, y, inside(x, y, 2) ? Terrain.Ground : Terrain.Sand, true);
    }
  }

  // A locked island is drawn as a silhouette in the haze: you can see there is something there,
  // you cannot read it and you cannot land on it.
  if (!unlocked) {
    for (let y = cy - radiusY - 2; y <= cy + radiusY + 2; y++) {
      for (let x = cx - radiusX - 2; x <= cx + radiusX + 2; x++) {
        if (inside(x, y, 0)) set(draft, x, y, Terrain.Rock, true);
      }
    }
    draft.islands.push({
      islandId: island.id,
      name: island.name,
      biome: island.biome,
      dock: { x: cx, y: cy + radiusY },
      label: { x: cx, y: cy },
      bounds: { x0: cx - radiusX, y0: cy - radiusY, x1: cx + radiusX, y1: cy + radiusY },
      locked: true,
    });
    return;
  }

  // Cliffs and trees around the rim, dense enough to shape the island, sparse enough to walk.
  for (let y = cy - radiusY - 1; y <= cy + radiusY + 1; y++) {
    for (let x = cx - radiusX - 1; x <= cx + radiusX + 1; x++) {
      if (!inside(x, y, 1) || inside(x, y, 3)) continue;
      const roll = hash(x, y, seed + 31);
      if (roll > 0.72) set(draft, x, y, Terrain.Tree, true);
      else if (roll > 0.64) set(draft, x, y, Terrain.Rock, true);
    }
  }

  // The dock sits on the south shore: the player always arrives from open water, facing up into
  // the island, which keeps the "one next step" in front of them rather than behind.
  const dockY = cy + radiusY;
  set(draft, cx, dockY, Terrain.Dock, true);
  set(draft, cx, dockY + 1, Terrain.Dock, true);

  const spots = layoutNodes(layout.length, cx, cy, radiusX, radiusY, seed, columns, (x, y) =>
    inside(x, y, 2),
  );

  // The trail runs from the dock through every spot in order, including the ones this path has no
  // task for. Skipping those would make the ground itself path-dependent again, which is the one
  // thing this function exists to avoid.
  let from = { x: cx, y: dockY };
  for (const spot of spots) {
    carvePath(draft, from, spot);
    from = spot;
  }

  spots.forEach((spot, index) => {
    const nodeId = layout[index];
    if (!nodeId) return;
    set(draft, spot.x, spot.y, Terrain.Path, true);
    const kind = markers.get(nodeId);
    // Not on this path. The spot stays trodden ground so both paths agree on the terrain; it just
    // has nothing standing on it, and anyone may walk over it.
    if (!kind) return;
    draft.markers.push({ nodeId, islandId: island.id, x: spot.x, y: spot.y, kind });
  });

  const landmark = { x: cx + Math.round(radiusX * 0.55), y: cy - Math.round(radiusY * 0.55) };
  set(draft, landmark.x, landmark.y, Terrain.Ground, true);
  set(draft, landmark.x, landmark.y - 1, Terrain.Ground, true);

  draft.islands.push({
    islandId: island.id,
    name: island.name,
    biome: island.biome,
    dock: { x: cx, y: dockY },
    label: { x: cx, y: cy - radiusY - 1 },
    bounds: { x0: cx - radiusX, y0: cy - radiusY, x1: cx + radiusX, y1: cy + radiusY },
    landmark,
    locked: false,
  });
}

/* ------------------------------------------------------------------ the drawn island */

/**
 * Lay a drawn island onto the world.
 *
 * The one judgement call in here is what happens where two islands overlap: painted land always
 * wins, painted water never does. An admin who nudges one island onto another's shore sees the
 * two coastlines merge rather than one of them punching a hole of open sea through the other,
 * which is both the friendlier failure and the one they can see and undo.
 */
function stampIsland(
  draft: Draft,
  island: ResolvedIsland,
  nodes: ResolvedNode[],
  unlocked: boolean,
  origin: { x: number; y: number },
): void {
  const drawn = island.world!;
  const left = island.map.x - Math.floor(drawn.width / 2) - origin.x;
  const top = island.map.y - Math.floor(drawn.height / 2) - origin.y;
  const toWorld = (x: number, y: number) => ({ x: left + x, y: top + y });

  for (let y = 0; y < drawn.height; y++) {
    for (let x = 0; x < drawn.width; x++) {
      const terrain = drawn.tiles[y * drawn.width + x] as Terrain;
      // A locked island is a silhouette in the haze: the shape is honest, nothing on it is legible.
      const painted = unlocked ? terrain : isWater(terrain) ? terrain : Terrain.Rock;
      set(draft, left + x, top + y, painted, !isWater(painted));
    }
  }

  const dock = toWorld(drawn.dock.x, drawn.dock.y);
  const bounds = { x0: left, y0: top, x1: left + drawn.width - 1, y1: top + drawn.height - 1 };
  const label = { x: left + Math.floor(drawn.width / 2), y: top - 1 };

  if (!unlocked) {
    draft.islands.push({
      islandId: island.id,
      name: island.name,
      biome: island.biome,
      dock,
      label: { x: label.x, y: top + Math.floor(drawn.height / 2) },
      bounds,
      locked: true,
    });
    return;
  }

  /**
   * Where each signpost stands. A node with no placement is content the checker already flags, so
   * this branch only runs on content published before the check existed or edited by hand — but it
   * still has to produce a marker, because a node the student can see on the island list and not
   * on the map is a worse failure than one standing somewhere slightly odd.
   */
  // The dock and everybody standing on the island are off-limits to the fallback below. It only
  // ever runs on content the checker already refuses, but "already broken" is not a licence to
  // drop a signpost on the tile the player spawns on or onto somebody's head.
  const taken = new Set<string>([`${drawn.dock.x},${drawn.dock.y}`]);
  for (const person of drawn.characters) taken.add(`${person.x},${person.y}`);

  for (const node of nodes) {
    const spot = drawn.spots.get(node.id);
    const local =
      spot && isStandable(tileOfLocal(drawn, spot.x, spot.y)) && !taken.has(`${spot.x},${spot.y}`)
        ? spot
        : nearestStandable(drawn, spot ?? drawn.dock, taken);
    if (!local) continue;
    taken.add(`${local.x},${local.y}`);
    const at = toWorld(local.x, local.y);
    draft.markers.push({
      nodeId: node.id,
      islandId: island.id,
      x: at.x,
      y: at.y,
      kind: node.kind,
    });
  }

  for (const person of drawn.characters) {
    const at = toWorld(person.x, person.y);
    draft.characters.push({
      id: person.id,
      islandId: island.id,
      art: person.art,
      name: person.name,
      facing: person.facing,
      lines: person.lines,
      x: at.x,
      y: at.y,
    });
  }

  for (const prop of drawn.props) {
    const at = toWorld(prop.x, prop.y);
    draft.props.push({ kind: prop.kind, x: at.x, y: at.y });
  }

  draft.islands.push({
    islandId: island.id,
    name: island.name,
    biome: island.biome,
    dock,
    label,
    bounds,
    locked: false,
  });
}

function tileOfLocal(drawn: NonNullable<ResolvedIsland['world']>, x: number, y: number): Terrain {
  if (x < 0 || y < 0 || x >= drawn.width || y >= drawn.height) return Terrain.Fog;
  return (drawn.tiles[y * drawn.width + x] ?? Terrain.Fog) as Terrain;
}

/**
 * Markers run in a serpentine from the dock upward: left to right, then right to left. It reads
 * as a route rather than a scatter, which is the whole reason the map exists.
 */
function layoutNodes(
  count: number,
  cx: number,
  cy: number,
  radiusX: number,
  radiusY: number,
  seed: number,
  columns: number,
  onLand: (x: number, y: number) => boolean,
): Array<{ x: number; y: number }> {
  if (count === 0) return [];
  const perRow = generatedPerRow(count, columns);
  const rows = Math.ceil(count / perRow);
  const spots: Array<{ x: number; y: number }> = [];
  const usableY = radiusY * 2 - 4;
  const stepY = rows > 1 ? usableY / rows : 0;

  for (let index = 0; index < count; index++) {
    const row = Math.floor(index / perRow);
    const inRow = index % perRow;
    const countInRow = Math.min(perRow, count - row * perRow);
    const leftToRight = row % 2 === 0;
    const t = countInRow === 1 ? 0.5 : inRow / (countInRow - 1);
    const across = leftToRight ? t : 1 - t;

    const spread = radiusX - 3;
    let x = Math.round(cx - spread + across * spread * 2);
    const y = Math.round(cy + radiusY - 3 - row * stepY - (hash(index, row, seed) > 0.5 ? 1 : 0));

    // The serpentine is laid out on a rectangle, but the land is an ellipse — the corner spots of
    // the top and bottom rows fall outside it. Walk each one back towards the centre until it is
    // on solid ground, so no marker ever ends up standing in the water.
    let guard = 0;
    while (!onLand(x, y) && guard < radiusX * 2) {
      x += x > cx ? -1 : 1;
      guard += 1;
      if (x === cx) break;
    }
    if (!onLand(x, y)) {
      x = cx;
    }
    // Two markers must never share a tile — the second would be unreachable.
    while (spots.some((s2) => s2.x === x && s2.y === y)) x += x >= cx ? -1 : 1;
    spots.push({ x, y });
  }
  return spots;
}

function carvePath(draft: Draft, from: { x: number; y: number }, to: { x: number; y: number }): void {
  let { x, y } = from;
  while (y !== to.y) {
    y += y < to.y ? 1 : -1;
    set(draft, x, y, Terrain.Path, true);
  }
  while (x !== to.x) {
    x += x < to.x ? 1 : -1;
    set(draft, x, y, Terrain.Path, true);
  }
}

/* ------------------------------------------------------------------ seeding the builder */

/**
 * The generated island, handed to the world builder as a starting point.
 *
 * This is the single most important thing about the builder and it is four lines of plumbing: the
 * canvas opens with the procedural island already on it — coastline, trees, the path from the dock
 * through every marker in order — and the admin edits from there. Nobody has ever drawn a good
 * island on an empty 26×20 grid, and asking them to would quietly turn "the world is editable"
 * into "the world is editable in theory".
 *
 * It is the same `carveIsland` the game runs, rendered into a canvas-sized origin instead of into
 * the archipelago, so what comes back is exactly what that island looks like today.
 */
export function generateIslandCanvas(
  island: IslandSeed,
  nodes: NodeSeed[],
): {
  width: number;
  height: number;
  tiles: Uint8Array;
  dock: { x: number; y: number };
  spots: Map<string, { x: number; y: number }>;
} {
  const shape = islandShape(island, nodes.length);
  const width = clampSize(shape.radiusX * 2 + 9);
  const height = clampSize(shape.radiusY * 2 + 9);

  // Put the island's centre at the canvas centre, which is exactly where `stampIsland` will read
  // it back from — so seeding and then publishing without touching anything is a no-op on screen.
  const origin = {
    x: island.map.x - Math.floor(width / 2),
    y: island.map.y - Math.floor(height / 2),
  };

  const draft: Draft = { tiles: new Map(), markers: [], islands: [], characters: [], props: [] };
  // The builder has always seeded from every node on the island rather than one path's worth, and
  // since the generator does the same the seed is now exactly the island that is on the map.
  carveIsland(
    draft,
    island,
    nodes.map((node) => node.id),
    new Map(nodes.map((node) => [node.id, node.kind] as const)),
    true,
    origin,
  );

  const tiles = new Uint8Array(width * height).fill(Terrain.Sea);
  for (const [key, terrain] of draft.tiles) {
    const x = key % 10000;
    const y = (key - x) / 10000;
    if (x < 0 || y < 0 || x >= width || y >= height) continue;
    tiles[y * width + x] = terrain;
  }

  const spots = new Map<string, { x: number; y: number }>();
  for (const marker of draft.markers)
    if (marker.x >= 0 && marker.y >= 0 && marker.x < width && marker.y < height)
      spots.set(marker.nodeId, { x: marker.x, y: marker.y });

  const placement = draft.islands[0];
  const dock = placement
    ? {
        x: Math.max(0, Math.min(width - 1, placement.dock.x)),
        y: Math.max(0, Math.min(height - 1, placement.dock.y)),
      }
    : { x: Math.floor(width / 2), y: height - 2 };

  return { width, height, tiles, dock, spots };
}

const clampSize = (value: number): number =>
  Math.max(MIN_ISLAND_SIZE, Math.min(MAX_ISLAND_SIZE, value));

/* ------------------------------------------------------------------ queries */

export function tileAt(world: World, x: number, y: number): Terrain {
  if (x < 0 || y < 0 || x >= world.width || y >= world.height) return Terrain.Fog;
  return world.tiles[y * world.width + x] as Terrain;
}

export function isWalkable(world: World, x: number, y: number, markerBlocks = true): boolean {
  const terrain = tileAt(world, x, y);
  if (isSolid(terrain)) return false;
  if (markerBlocks && world.markers.some((m) => m.x === x && m.y === y)) return false;
  return true;
}

/**
 * Somebody standing here. Like peers, characters are drawn but never solid — you walk through
 * them and face them to talk — so no arrangement of people can wall a player off from anything.
 */
export function characterAt(world: World, x: number, y: number): PlacedCharacter | undefined {
  return world.characters.find((person) => person.x === x && person.y === y);
}

export function markerAt(world: World, x: number, y: number): Marker | undefined {
  return world.markers.find((m) => m.x === x && m.y === y);
}

export function islandAt(world: World, x: number, y: number): IslandPlacement | undefined {
  return world.islands.find(
    (i) => x >= i.bounds.x0 && x <= i.bounds.x1 && y >= i.bounds.y0 && y <= i.bounds.y1,
  );
}

export const worldHash = hash;

/* ------------------------------------------------------------------ the crew, placed */

export interface PlacedPeer {
  id: string;
  name: string;
  look: number;
  souvenirs: readonly string[];
  x: number;
  y: number;
}

/**
 * Where somebody else stands.
 *
 * Beside their marker, never on it: a person standing on a signpost hides the signpost, and worse,
 * four of them around one would fence it off. Peers are drawn but never solid — you walk straight
 * through them and face them to talk, the way every game with a village does it — so no arrangement
 * of people can ever wall a player away from their own next step.
 *
 * The offsets are tried in a fixed order and the peers arrive pre-sorted by a hash of their id, so
 * the same crowd lands in the same arrangement on every redraw. A person who jumps to the other
 * side of a tree between frames reads as a rendering bug, not as a neighbour.
 */
/**
 * The tile directly below a marker is deliberately not in this list.
 *
 * That is the spot you stand on to read a signpost — the map opens the player on it, and it is
 * where anyone walking up to a marker naturally ends up. Peers are drawn under the hero when they
 * share a tile, so a peer parked there is simply invisible, which reads as the crew failing to
 * load. Leaving the reading spot free costs one slot out of eight and nothing else.
 *
 * The tile directly above is out for a different reason: markers are drawn taller than their tile,
 * so anybody standing there is behind the signpost.
 */
const PEER_SLOTS: ReadonlyArray<readonly [number, number]> = [
  [-1, 0],
  [1, 0],
  [-1, 1],
  [1, 1],
  [-2, 0],
  [2, 0],
  [-2, 1],
  [2, 1],
  [0, 2],
];

export function placePeers(
  world: World,
  peers: ReadonlyArray<{
    id: string;
    name: string;
    look: number;
    souvenirs: readonly string[];
    nodeId: string | null;
    islandId: string | null;
  }>,
  max: number,
): { placed: PlacedPeer[]; crowds: Map<string, { x: number; y: number; extra: number }> } {
  const markers = new Map(world.markers.map((marker) => [marker.nodeId, marker]));
  const docks = new Map(world.islands.map((island) => [island.islandId, island.dock]));

  const placed: PlacedPeer[] = [];
  const crowds = new Map<string, { x: number; y: number; extra: number }>();
  /**
   * Tiles somebody is already standing on.
   *
   * Seeded with the authored characters, because `interact()` resolves a character before a peer:
   * a crew member who lands on Marko's tile is drawn there and can never be tapped or talked to.
   * The admin who placed Marko has no way to know a peer will arrive later, so the peer moves.
   */
  const taken = new Set<string>(world.characters.map((person) => `${person.x},${person.y}`));
  const perAnchor = new Map<string, number>();

  for (const peer of peers) {
    const anchor = (peer.nodeId ? markers.get(peer.nodeId) : undefined) ??
      (peer.islandId ? docks.get(peer.islandId) : undefined);
    if (!anchor) continue;

    const key = `${anchor.x},${anchor.y}`;
    const already = perAnchor.get(key) ?? 0;
    if (already >= max) {
      const crowd = crowds.get(key) ?? { x: anchor.x, y: anchor.y, extra: 0 };
      crowd.extra += 1;
      crowds.set(key, crowd);
      continue;
    }

    const slot = PEER_SLOTS.find(([dx, dy]) => {
      const x = anchor.x + dx;
      const y = anchor.y + dy;
      if (taken.has(`${x},${y}`)) return false;
      // markerBlocks stays on: standing on somebody's signpost is exactly what this avoids.
      return isWalkable(world, x, y);
    });
    // Nowhere to stand — a marker wedged against a cliff. Better to leave a person off the map
    // than to draw them inside a rock; they are still on the crew screen.
    if (!slot) continue;

    const x = anchor.x + slot[0];
    const y = anchor.y + slot[1];
    taken.add(`${x},${y}`);
    perAnchor.set(key, already + 1);
    placed.push({ id: peer.id, name: peer.name, look: peer.look, souvenirs: peer.souvenirs, x, y });
  }

  return { placed, crowds };
}

export function peerAt(placed: readonly PlacedPeer[], x: number, y: number): PlacedPeer | undefined {
  return placed.find((peer) => peer.x === x && peer.y === y);
}
